import { useState, useEffect, useMemo } from "react";
import { showSuccess, showError } from "@/lib/utils/notifications";
import { useAppSelector } from "@/app/hooks";
import { selectToken, selectUser } from "@/features/auth/authSlice";
import { getAuthTokenFromStorage } from "@/lib/utils/storage";
import { usePermission } from "@/hooks/usePermission";
import { openFilePreviewByPath } from "@/lib/utils/filePreview";
import {
  getCreditBureauConfig,
  getCreditReportAddressRequest,
  regenerateCreditReport,
  retryFailedCreditJob,
  updateApplicantCreditScore,
  addUnderwriterApplicantNote,
  getUnderwriterNotes,
  fetchNinjaListForApplicant,
} from "../services/applicationService";
import {
  buildAngularCreditReportIframeSrc,
  shouldShowAngularCreditReportHtmlIframe,
} from "../utils/creditReportIframeUrl";
import {
  extractCreditReportJobMeta,
  hasZipReportCreditReportMeta,
  type CreditReportApplicationData,
  creditReportHtmlIframeRevision,
  resolveCreditReportHistory,
  shouldShowCreditReportEmptyState,
  shouldShowOpenCreditReportButton,
  isOpenCreditReportDisabled,
  resolveOpenCreditReportPath,
  openCreditReportMayRegenerate,
  openCreditReportDisabledTooltip,
  parseConfigCreditBureau,
  shouldShowResubmitCreditReportButton,
  shouldShowCoreLogicSection,
  shouldShowCoreLogicSectionTitle,
  hasCoreLogicApplicantScoreData,
} from "../utils/creditReportHelpers";
import { applyTaggingToNotes } from "@/components/notes/MentionTextarea";
import { getNoteRowId, canEditNote } from "../utils/notesTabHelpers";
import { LIBERTY_ROLES } from "@/constants/roles";
import { CORELOGIC_SCORE_EDIT_ROLES } from "../utils/applicationConstants";
import type {
  CreditReportTabProps,
  CreditBureauType,
  CreditReportHistoryRow,
} from "../types/tabs";
import type { EditableUwNote } from "../types/hooks";

export function useCreditReportTab({
  applicationAllData,
  applicationData,
  applicantId,
  onRefresh,
}: CreditReportTabProps) {
  const user = useAppSelector(selectUser);
  const authToken = useAppSelector(selectToken);
  const { hasPolicy } = usePermission();
  const currentUserRole = user?.current_role?.role_name || "";
  const canResubmitCreditReport = hasPolicy(["application_detail_create_credit_report"]);
  const canInputScore = CORELOGIC_SCORE_EDIT_ROLES.includes(currentUserRole);
  const isAdmin = currentUserRole === "ROLE_ADMIN";
  const isLibertyRole = (LIBERTY_ROLES as readonly string[]).includes(currentUserRole);
  const currentUserId = user?.user_id ?? null;

  const [uwNotesEditClock, setUwNotesEditClock] = useState(0);
  const [editingUnderwriterNote, setEditingUnderwriterNote] =
    useState<EditableUwNote | null>(null);
  const [processing, setProcessing] = useState(false);
  const [openingReport, setOpeningReport] = useState(false);
  const [savingScore, setSavingScore] = useState(false);
  const [underwritingNote, setUnderwritingNote] = useState("");
  const [underwritingTaggingContent, setUnderwritingTaggingContent] = useState<
    Record<string, string>
  >({});
  const [submittingNote, setSubmittingNote] = useState(false);
  const [fetchedUnderwriterNotes, setFetchedUnderwriterNotes] = useState<unknown[] | null>(null);
  const [customScore, setCustomScore] = useState(
    applicationData?.corelogic_applicant_score_custom || "",
  );
  const [configDetail, setConfigDetail] = useState<{
    active: CreditBureauType;
  } | null>(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [ninjaListLength, setNinjaListLength] = useState(0);
  const [resubmitConfirm, setResubmitConfirm] = useState<{
    open: boolean;
    message: string;
    accessExisting: boolean;
  }>({ open: false, message: "", accessExisting: false });
  const [saveScoreConfirm, setSaveScoreConfirm] = useState<{
    open: boolean;
    score: string;
  }>({ open: false, score: "" });

  const refreshAfterAction = async () => {
    if (onRefresh) {
      await onRefresh();
    } else {
      window.location.reload();
    }
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cfg = await getCreditBureauConfig();
        if (cancelled) return;
        const bureau = parseConfigCreditBureau(
          (cfg?.active as string | undefined) ?? null,
        );
        setConfigDetail(bureau ? { active: bureau } : null);
      } catch {
        if (!cancelled) setConfigDetail(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applicantId]);

  const htmlReportUrl = useMemo(
    () =>
      buildAngularCreditReportIframeSrc(
        applicantId,
        authToken ?? getAuthTokenFromStorage(),
      ),
    [applicantId, authToken],
  );

  useEffect(() => {
    let cancelled = false;
    if (!applicantId?.trim()) {
      setNinjaListLength(0);
      return () => {
        cancelled = true;
      };
    }
    void fetchNinjaListForApplicant(applicantId).then((rows) => {
      if (!cancelled) setNinjaListLength(Array.isArray(rows) ? rows.length : 0);
    });
    return () => {
      cancelled = true;
    };
  }, [applicantId]);

  const canViewUnderwritingNotesSection = hasPolicy([
    "application_detail_add_underwriting_notes_view",
  ]);

  useEffect(() => {
    if (!applicantId?.trim() || !canViewUnderwritingNotesSection) return;
    let cancelled = false;
    void (async () => {
      try {
        const rows = await getUnderwriterNotes(applicantId);
        if (cancelled) return;
        setFetchedUnderwriterNotes(rows);
      } catch {
        if (cancelled) return;
        setFetchedUnderwriterNotes(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicantId, canViewUnderwritingNotesSection]);

  useEffect(() => {
    if (!canViewUnderwritingNotesSection) return;
    const t = window.setInterval(() => {
      setUwNotesEditClock((n) => n + 1);
    }, 30_000);
    return () => window.clearInterval(t);
  }, [canViewUnderwritingNotesSection]);

  const creditReportHistory = resolveCreditReportHistory(
    applicationData as Record<string, unknown> | null | undefined,
    applicationAllData as Record<string, unknown> | null | undefined,
  ) as CreditReportHistoryRow[];
  const deniedCheckboxData = applicationData?.denied_checkbox_data;

  const embeddedUnderwriterNotes = applicationData?.underwriter_notes;
  const underwriterNotesList = (
    fetchedUnderwriterNotes ??
    (Array.isArray(embeddedUnderwriterNotes) ? embeddedUnderwriterNotes : [])
  ) as Array<Record<string, unknown>>;

  const filteredUnderwriterNotes = useMemo(() => {
    return underwriterNotesList.filter((note) => {
      const text = String(note.notes ?? note.note ?? note.note_text ?? "");
      if (!isAdmin && text.includes("ITR Ratio:")) return false;
      return true;
    });
  }, [underwriterNotesList, isAdmin, uwNotesEditClock]);

  const canShowEditUnderwriterNote = (note: Record<string, unknown>) => {
    const raw = String(note.notes ?? note.note ?? note.note_text ?? "");
    if (raw.startsWith("Risk Rating changed")) return false;
    return (
      canEditNote(note as Parameters<typeof canEditNote>[0], {
        isLibertyRole,
        currentUserId,
      }) && getNoteRowId(note) != null
    );
  };

  const appRecord = applicationData as Record<string, unknown> | null | undefined;

  const creditReportFlag = appRecord?.credit_report;
  const hasZipReport = creditReportFlag === "1" || creditReportFlag === 1;
  const hasCoreLogic = Boolean(
    applicationData?.corelogic_credit_report_path ||
      applicationData?.corelogic_applicant_score === 0 ||
      applicationData?.corelogic_applicant_score === "0" ||
      applicationData?.corelogic_applicant_score,
  );
  const hasEquifax = Boolean(
    applicationData?.equifax_credit_report_path ||
      applicationData?.equifax_score === 0 ||
      applicationData?.equifax_score === "0" ||
      applicationData?.equifax_score,
  );
  const showCreditReportEmptyState = shouldShowCreditReportEmptyState(
    applicationData as CreditReportApplicationData | null,
  );

  const hasPayment = !!applicationData?.payment_log_id;

  const jobMeta = extractCreditReportJobMeta(
    applicationData as Record<string, unknown> | null | undefined,
    applicationAllData as Record<string, unknown> | null | undefined,
  );
  const { hasQueuedJob, hasFailedJob, hasCreditChanged, failedJobId: failedJobIdForZip } =
    jobMeta;

  const showCoreLogicSection = shouldShowCoreLogicSection(
    applicationData as CreditReportApplicationData | null,
    configDetail?.active ?? null,
  );

  const showCoreLogicSectionTitle = shouldShowCoreLogicSectionTitle(
    applicationData as CreditReportApplicationData | null,
    configDetail?.active ?? null,
  );

  const showCoreLogicApplicantScore = hasCoreLogicApplicantScoreData(
    applicationData as CreditReportApplicationData | null,
  );

  const handleOpenCreditReport = async (path?: string) => {
    const configActive = configDetail?.active ?? null;
    const reportPath =
      path ||
      resolveOpenCreditReportPath(
        applicationData as CreditReportApplicationData | null,
        configActive,
      ) ||
      undefined;

    if (reportPath) {
      openFilePreviewByPath(String(reportPath));
      return;
    }

    if (
      !openCreditReportMayRegenerate(
        applicationData as CreditReportApplicationData | null,
        configActive,
      )
    ) {
      return;
    }

    setOpeningReport(true);
    try {
      let addressId: string | undefined;
      try {
        const response = await getCreditReportAddressRequest(applicantId);
        const list = Array.isArray(response)
          ? response
          : response &&
              typeof response === "object" &&
              Array.isArray((response as { addresses?: unknown }).addresses)
            ? ((response as { addresses: unknown[] }).addresses)
            : [];
        const first =
          list[0] && typeof list[0] === "object"
            ? (list[0] as Record<string, unknown>)
            : null;
        if (first?.ra_id != null && String(first.ra_id).trim() !== "") {
          addressId = String(first.ra_id);
        }
      } catch {
        /* Angular `openCreditReport` error path still calls executeJsonRequest() without address */
      }

      await regenerateCreditReport(applicantId, {
        report_type: "json",
        ...(addressId ? { address_id: addressId } : {}),
      });
      showSuccess(
        "Credit report has been fetched. Please check Notes if the credit report fails.",
      );
      await refreshAfterAction();
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to open or request credit report",
      );
    } finally {
      setOpeningReport(false);
    }
  };

  const resubmitZipCreditReport = async (jobId?: string) => {
    setProcessing(true);
    try {
      if (jobId) {
        await retryFailedCreditJob(jobId);
      } else {
        await regenerateCreditReport(applicantId, { report_type: "json" });
      }
      showSuccess(
        "Credit report has been fetched. Please check Notes if the credit report fails.",
      );
      await refreshAfterAction();
    } catch (error: unknown) {
      showError(
        error instanceof Error ? error.message : "Failed to resubmit credit report",
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleResubmitZipReport = () => {
    if (
      !hasZipReportCreditReportMeta(
        applicationAllData as Record<string, unknown> | null | undefined,
      )
    ) {
      return;
    }

    let confirmMessage = "Do you want to Resubmit the Credit Report?";
    let hasOldReport = false;

    if (applicationData?.credit_report_path) {
      confirmMessage =
        "Do you want to access the previous report or generate a new one?";
      hasOldReport = true;
    }

    if (hasQueuedJob) {
      confirmMessage =
        "This Credit Report is still in Queue to be processed. Do you want to cancel the original request and requeue it with corrected data?";
      hasOldReport = false;
    } else if (!hasCreditChanged && hasFailedJob) {
      confirmMessage =
        "Previous Credit Report job is not ready to Review, Do you want to retry job?";
      hasOldReport = false;
    }

    const message = hasOldReport
      ? `${confirmMessage}\n\nClick "Access existing" to open the previous report, or "Generate new" to resubmit.`
      : confirmMessage;

    setResubmitConfirm({ open: true, message, accessExisting: hasOldReport });
  };

  const performResubmitZipReportPrimary = async () => {
    const { accessExisting } = resubmitConfirm;
    setResubmitConfirm({ open: false, message: "", accessExisting: false });
    if (accessExisting) {
      void handleOpenCreditReport(
        applicationData?.credit_report_path ?? undefined,
      );
      return;
    }
    await resubmitZipCreditReport(
      hasFailedJob && failedJobIdForZip ? failedJobIdForZip : undefined,
    );
  };

  const performResubmitZipReportSecondary = async () => {
    const { accessExisting } = resubmitConfirm;
    setResubmitConfirm({ open: false, message: "", accessExisting: false });
    if (!accessExisting) return;
    await resubmitZipCreditReport(
      hasFailedJob && failedJobIdForZip ? failedJobIdForZip : undefined,
    );
  };

  const handleResubmitCoreLogic = () => {
    setShowAddressModal(true);
  };

  const creditBureauForResubmit = configDetail?.active ?? null;

  const showStepperResubmit = shouldShowResubmitCreditReportButton({
    configActive: creditBureauForResubmit,
    hasPayment,
    canResubmitCreditReport,
  });

  const stepperResubmitHandler =
    creditBureauForResubmit === "ZipReport"
      ? handleResubmitZipReport
      : handleResubmitCoreLogic;

  const stepperResubmitLabel =
    processing
      ? "Processing..."
      : creditBureauForResubmit === "ZipReport"
        ? "Resubmit Credit Report"
        : creditBureauForResubmit === "CoreLogic"
          ? !applicationData?.corelogic_credit_report_path
            ? "Create Credit Report"
            : "Resubmit Credit Report"
          : creditBureauForResubmit === "Equifax"
            ? !applicationData?.equifax_score
              ? "Create Credit Report"
              : "Resubmit Credit Report"
            : "Resubmit Credit Report";

  const configActive = configDetail?.active ?? null;
  const creditReportAppData = applicationData as CreditReportApplicationData | null;

  const showOpenCreditReportButton = shouldShowOpenCreditReportButton(
    creditReportAppData,
    configActive,
  );

  const openCreditReportDisabled =
    openingReport || isOpenCreditReportDisabled(creditReportAppData, configActive);

  const openCreditReportTooltip = openCreditReportDisabledTooltip(
    creditReportAppData,
    configActive,
  );

  const handleSaveScore = () => {
    if (!customScore) {
      showError("Please enter a score");
      return;
    }
    setSaveScoreConfirm({ open: true, score: String(customScore) });
  };

  const performSaveScore = async () => {
    setSaveScoreConfirm({ open: false, score: "" });
    setSavingScore(true);
    try {
      await updateApplicantCreditScore(applicantId, {
        corelogic_applicant_score_custom: customScore,
      });
      showSuccess("Applicant Score Saved Successfully");
      await refreshAfterAction();
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : "Failed to save score");
    } finally {
      setSavingScore(false);
    }
  };

  const handleAddUnderwritingNote = async () => {
    if (!underwritingNote.trim()) {
      showError("Please enter a note");
      return;
    }

    setSubmittingNote(true);
    try {
      const noteBody = applyTaggingToNotes(
        underwritingNote.trim(),
        underwritingTaggingContent,
      );
      await addUnderwriterApplicantNote(applicantId, noteBody);
      showSuccess("Note Added Successfully!");
      setUnderwritingNote("");
      setUnderwritingTaggingContent({});
      try {
        const rows = await getUnderwriterNotes(applicantId);
        setFetchedUnderwriterNotes(rows);
      } catch {
        setFetchedUnderwriterNotes(null);
      }
      await refreshAfterAction();
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : "Failed to add note");
    } finally {
      setSubmittingNote(false);
    }
  };

  const refreshUnderwriterNotes = async () => {
    try {
      const rows = await getUnderwriterNotes(applicantId);
      setFetchedUnderwriterNotes(rows);
    } catch {
      setFetchedUnderwriterNotes(null);
    }
  };

  const showAngularHtmlIframe =
    htmlReportUrl != null &&
    shouldShowAngularCreditReportHtmlIframe(
      appRecord,
      ninjaListLength,
      applicationAllData as Record<string, unknown> | null | undefined,
    );

  const showCreditReportBureauActions =
    showAngularHtmlIframe || showStepperResubmit || showOpenCreditReportButton;

  return {
    applicationAllData,
    applicationData,
    applicantId,
    canInputScore,
    canViewUnderwritingNotesSection,
    creditReportHistory,
    customScore,
    deniedCheckboxData,
    editingUnderwriterNote,
    filteredUnderwriterNotes,
    hasEquifax,
    hasCoreLogic,
    hasZipReport,
    htmlReportUrl,
    openCreditReportDisabled,
    openCreditReportTooltip,
    openingReport,
    processing,
    resubmitConfirm,
    saveScoreConfirm,
    savingScore,
    showAngularHtmlIframe,
    showCoreLogicSection,
    showCoreLogicSectionTitle,
    showCoreLogicApplicantScore,
    showCreditReportBureauActions,
    showCreditReportEmptyState,
    showOpenCreditReportButton,
    showStepperResubmit,
    stepperResubmitHandler,
    stepperResubmitLabel,
    submittingNote,
    underwritingNote,
    showAddressModal,
    appRecord,
    creditReportHtmlIframeRevision: creditReportHtmlIframeRevision(appRecord),
    canShowEditUnderwriterNote,
    handleAddUnderwritingNote,
    handleOpenCreditReport,
    handleSaveScore,
    performResubmitZipReportPrimary,
    performResubmitZipReportSecondary,
    performSaveScore,
    refreshAfterAction,
    refreshUnderwriterNotes,
    setCustomScore,
    setEditingUnderwriterNote,
    setResubmitConfirm,
    setSaveScoreConfirm,
    setShowAddressModal,
    setUnderwritingNote,
    setUnderwritingTaggingContent,
  };
}

export type CreditReportTabState = ReturnType<typeof useCreditReportTab>;
